import { has, pick } from 'lodash';

import { NumberHelper } from 'core/helpers';
import { DiscountApplyToTypes } from 'corporate/enums';
import {
  PricingProfilePricingType,
  PricingSourceDiscountType,
  PricingSourceStatus,
  PricingSourceType,
} from 'pricing/enums';
import { defaultSpaAgreementFields } from 'quotes/constants';
import { QuotePartUnifiedStatus } from 'quotes/enums';
import { sanitizeAgreementText } from 'quotes/mappers/tradeAgreement';
import {
  ICustomerQuotePart,
  IDefaultSpaAgreementFormValue,
  IDefaultSpaAgreementPayload,
  IQuotePartCostData,
  IQuotePartPriceData,
  ISelectedSpaFormValue,
  ITradeAgreement,
} from 'quotes/types';
import { getCostSourceTypeInUse } from 'quotes/utils';

import { QuotePartHelper } from './QuotePartHelper';

// The response only echoes the cost the user entered: no default to revert to, no external base.
const isEnteredCostSourceType = (costSourceType?: PricingSourceType | null): boolean =>
  PricingSourceType.CUSTOM === costSourceType || PricingSourceType.SELECTED_SPA === costSourceType;

// Rebuilt from the row, so a re-save of an unchanged pick re-posts its identity at its own cost.
const mapQuotePartToSelectedSpa = (quotePart: ICustomerQuotePart): ISelectedSpaFormValue | null => {
  if (PricingSourceType.SELECTED_SPA !== getCostSourceTypeInUse(quotePart)) {
    return null;
  }

  // Vendor text that lands in html-react-parser sinks.
  return {
    // Nothing else stores the pick's cost: it is the line's own Quoted Cost.
    spaCost: quotePart?.purchaseCost,
    // Start and end live in the shared cost-source validity columns, not in spa_* ones.
    spaCostEndDate: quotePart?.costSourceValidUntil,
    spaCostRegistrationNum: sanitizeAgreementText(quotePart?.costSourceSpaCostRegistrationNum),
    spaCostSource: sanitizeAgreementText(quotePart?.costSourceSpaCostSource),
    spaCostSourceId: quotePart?.costSourceSpaCostSourceId,
    spaCostSourceName: sanitizeAgreementText(quotePart?.costSourceSpaCostSourceName),
    spaCostStartDate: quotePart?.costSourceValidSince,
  };
};

const holdsSelectedSpa = (costData?: IQuotePartCostData | null): boolean =>
  PricingSourceType.SELECTED_SPA === costData?.costSourceType && !!costData?.selectedSpa;

const selectedSpaFormFields: (keyof ISelectedSpaFormValue)[] = [
  'baseUomSpaCost',
  'originalSpaCost',
  'spaCost',
  'spaCostCurrency',
  'spaCostEndDate',
  'spaCostRegistrationNum',
  'spaCostSource',
  'spaCostSourceId',
  'spaCostSourceName',
  'spaCostStartDate',
  'spaCostTaName',
  'spaCostUom',
  'spaQuantityRemaining',
  'spaQuantityUsage',
  'uomConversionFactor',
];

// Clears the cost's vendor quote with its dates, so none outlives it.
export const EMPTY_VENDOR_QUOTE_COST_DATA: Pick<
  IQuotePartCostData,
  | 'costSourceQuoteExternalNumber'
  | 'costSourceQuoteId'
  | 'costSourceQuoteStartDate'
  | 'costSourceQuoteValidUntil'
  | 'costSourceSharedValidUntil'
> = {
  costSourceQuoteExternalNumber: null,
  costSourceQuoteId: null,
  costSourceQuoteStartDate: null,
  costSourceQuoteValidUntil: null,
  costSourceSharedValidUntil: null,
};

export const QuotePartPricingHelper = {
  mapQuotePartToSelectedSpa,

  /**
   * Narrowed, so a key the response grows later cannot ride into the save payload unnoticed.
   */
  mapTradeAgreementToSelectedSpa: (agreement: ITradeAgreement): ISelectedSpaFormValue =>
    pick(agreement, selectedSpaFormFields) as ISelectedSpaFormValue,

  mapCostDataToDefaultSpaAgreement: (
    costData?: IQuotePartCostData | null
  ): IDefaultSpaAgreementFormValue =>
    pick(costData, [...defaultSpaAgreementFields, 'externalSpaCostUom']),

  // `isAlternateVersionEnabled` is passed in because the /prices response carries no
  // alternateVersionEnabled of its own, and the lock gate needs it (see costLocked below).
  // `uom` is the one the response was asked for.
  mapPricesUomToCostData: (
    pricesUom: Record<string, unknown>,
    isAlternateVersionEnabled: boolean,
    prevCostData?: IQuotePartCostData,
    uom?: string | null
  ): IQuotePartCostData => {
    const rawCostSourceType = (pricesUom?.costSourceType ||
      pricesUom?.costSource) as PricingSourceType;

    // The BE may return stringy zeros (e.g. "0.00000") for missing cost fields, so every cost read
    // more than once is coerced here and read from these locals below.
    const responsePurchaseCost = Number(pricesUom?.purchaseCost) || null;
    const responseExternalPurchaseCost = Number(pricesUom?.externalPurchaseCost) || null;
    const responseExternalReplacementCost = Number(pricesUom?.externalReplacementCost) || null;
    const responseExternalSpaCost = Number(pricesUom?.externalSpaCost) || null;

    // BE returns EXTERNAL even when no external cost was found; drop the source so
    // it surfaces as "No Cost" instead of a phantom external source. Any external cost
    // field (purchase, standard, replacement, or SPA) counts as a real source.
    const hasCost =
      responsePurchaseCost > 0 ||
      Number(pricesUom?.cost) > 0 ||
      responseExternalReplacementCost > 0 ||
      responseExternalSpaCost > 0;
    const costSourceType =
      rawCostSourceType === PricingSourceType.EXTERNAL && !hasCost ? null : rawCostSourceType;

    // external_purchase_cost carries the vendor's baseCost — the raw value the Overwrite-off revert
    // must put back on the wire. Keep the previous one when this response cannot supply a
    // trustworthy replacement, which happens in two distinct ways:
    //  (a) it is absent — the BE only has a baseCost to map when the vendor supplied one, and an
    //      unsupplemented T-item has none;
    //  (b) it collapsed to the cost we just sent — the vendor's "lowest of Replacement / SPA /
    //      Entered" resolves to Entered when replacement and SPA are both empty, so a CUSTOM
    //      response hands our own value straight back. Adopting that circular value would re-break
    //      the revert, since the BE would then never see the real base cost again.
    // Everything else is fresh discovery and must win — including a CUSTOM response with empty
    // replacement/SPA whose external cost DIFFERS from the echo (a real T-item baseCost).
    const isCircularExternalCost =
      isEnteredCostSourceType(costSourceType) &&
      !responseExternalReplacementCost &&
      !responseExternalSpaCost &&
      null !== responseExternalPurchaseCost &&
      responseExternalPurchaseCost === responsePurchaseCost;

    const shouldKeepPreviousExternalPurchaseCost =
      null === responseExternalPurchaseCost || isCircularExternalCost;

    // An answer for the held pick omits the default's keys, so the form keeps its own; a present
    // null still clears it.
    const isDefaultSpaAgreementWithheld = holdsSelectedSpa(prevCostData);
    const isKeptDefaultSpaAgreementField = (field: keyof IDefaultSpaAgreementPayload): boolean =>
      isDefaultSpaAgreementWithheld && !has(pricesUom, field);
    const getDefaultSpaAgreementField = <K extends keyof IDefaultSpaAgreementPayload>(
      field: K,
      responseValue: IDefaultSpaAgreementPayload[K]
    ): IDefaultSpaAgreementPayload[K] =>
      isKeptDefaultSpaAgreementField(field) ? prevCostData[field] : responseValue;

    return {
      defaultPurchaseCost: !isEnteredCostSourceType(costSourceType)
        ? Number(pricesUom?.purchaseCost ?? 0)
        : prevCostData?.defaultPurchaseCost,
      // Mirrors QuotePartHelper.isCostLocked: the contract lock only applies to an external source
      // on an alternate-version company. Both guards are required — CostLockTooltip ORs this flag
      // with the canonical helper, so a true here short-circuits past the helper's own check.
      // Read the derived costSourceType, not the raw one, so a source dropped as phantom above can
      // never come back as a lock icon on a line with no external cost.
      costLocked:
        isAlternateVersionEnabled &&
        PricingSourceType.EXTERNAL === costSourceType &&
        !!pricesUom?.externalCostLocked,
      costQuantityDiscountsApplied: pricesUom?.costQuantityDiscountsApplied ? 1 : 0,
      costSourceDiscountApplyTo: pricesUom?.costSourceDiscountApplyTo as DiscountApplyToTypes,
      costSourceDiscountType: pricesUom?.costSourceDiscountType as PricingSourceDiscountType,
      costSourceDiscountValue: pricesUom?.costSourceDiscountValue as number,
      costSourceId: pricesUom?.costSourceId as number,
      costSourcePricingProfileId: pricesUom?.costSourcePricingProfileId as number,
      costSourcePricingProfileName: pricesUom?.costSourcePricingProfileName as string,
      costSourcePricingProfilePricingType:
        pricesUom?.costSourcePricingProfilePricingType as PricingProfilePricingType,
      costSourceQuoteExternalNumber: (pricesUom?.costSourceQuoteExternalNumber as string) || null,
      costSourceQuoteId: Number(pricesUom?.costSourceQuoteId) || null,
      costSourceQuoteStartDate: (pricesUom?.costSourceQuoteStartDate as string) || null,
      // Undefined, not null, while the answer leaves the keys out (the part pick's validate-part):
      // the dates are unknown then, not missing from the vendor quote.
      costSourceQuoteValidUntil: has(pricesUom, 'costSourceQuoteValidUntil')
        ? (pricesUom.costSourceQuoteValidUntil as string) || null
        : undefined,
      costSourceSharedValidUntil: (pricesUom?.costSourceSharedValidUntil as string) || null,
      costSourceStatus: pricesUom?.costSourceStatus as PricingSourceStatus,
      costSourceType,
      externalPurchaseCost: shouldKeepPreviousExternalPurchaseCost
        ? prevCostData?.externalPurchaseCost ?? null
        : responseExternalPurchaseCost,
      externalRepCostEndDate: pricesUom?.externalRepCostEndDate as string,
      externalRepCostRegistrationNum: pricesUom?.externalRepCostRegistrationNum as string,
      externalRepCostSource: pricesUom?.externalRepCostSource as string,
      externalRepCostSourceName: pricesUom?.externalRepCostSourceName as string,
      externalReplacementCost: responseExternalReplacementCost,
      externalSpaCost: getDefaultSpaAgreementField('externalSpaCost', responseExternalSpaCost),
      externalSpaCostEndDate: getDefaultSpaAgreementField(
        'externalSpaCostEndDate',
        pricesUom?.externalSpaCostEndDate as string
      ),
      externalSpaCostRegistrationNum: getDefaultSpaAgreementField(
        'externalSpaCostRegistrationNum',
        pricesUom?.externalSpaCostRegistrationNum as string
      ),
      externalSpaCostSource: getDefaultSpaAgreementField(
        'externalSpaCostSource',
        pricesUom?.externalSpaCostSource as string
      ),
      externalSpaCostSourceName: getDefaultSpaAgreementField(
        'externalSpaCostSourceName',
        pricesUom?.externalSpaCostSourceName as string
      ),
      externalSpaCostUom: isKeptDefaultSpaAgreementField('externalSpaCost')
        ? prevCostData?.externalSpaCostUom
        : uom ?? null,
    };
  },

  // Deferred replacement-standard-cost lifecycle. A stateless /quote/part/prices preview no
  // longer returns externalReplacementStandardCost (the BE materializes it only at its
  // need-points), so an absent value means "not recomputed", NOT "cleared". Decide what the
  // form's replacementStandardCost should become when a cost response lands:
  //  - response carries a value          → trust it
  //  - absent + replacement cost same    → preserve the current derived value (deferred)
  //  - absent + replacement cost changed → the old value is stale: clear it (external freshness,
  //                                        when needed, arrives in the same response via the BE
  //                                        fetch_replacement_standard_cost flag, and manual-basis
  //                                        lines re-derive at the end of calculatePrices)
  // Costs are compared at COST precision, not price precision — the two are distinct company
  // settings and the value being diffed is a cost.
  resolveReplacementStandardCostUpdate: ({
    priorReplacementStandardCost,
    priorReplacementCost,
    responseReplacementCost,
    responseReplacementStandardCost,
    costDecimals,
  }: {
    priorReplacementStandardCost: number | null;
    priorReplacementCost: number | null;
    responseReplacementCost: number | null;
    responseReplacementStandardCost: number | null;
    costDecimals: number;
  }): { nextReplacementStandardCost: number | null } => {
    if (responseReplacementStandardCost) {
      return { nextReplacementStandardCost: responseReplacementStandardCost };
    }

    const replacementCostChanged = !NumberHelper.areEqual(
      priorReplacementCost ?? 0,
      responseReplacementCost ?? 0,
      costDecimals
    );

    if (!replacementCostChanged) {
      return { nextReplacementStandardCost: priorReplacementStandardCost };
    }

    return { nextReplacementStandardCost: null };
  },

  mapQuotePartToCostData: (
    quotePart: ICustomerQuotePart
  ): { costData: IQuotePartCostData; unifiedPartStatus: QuotePartUnifiedStatus } => ({
    costData: {
      costLocked: QuotePartHelper.isCostLocked(quotePart),
      costQuantityDiscountsApplied: quotePart?.costQuantityDiscountsApplied,
      costSourceDiscountApplyTo: quotePart?.costSourceDiscountApplyTo,
      costSourceDiscountType: quotePart?.costSourceDiscountType,
      costSourceDiscountValue: quotePart?.costSourceDiscountValue,
      costSourceId: quotePart?.costSourceId,
      costSourcePricingProfileId: quotePart?.costSourcePricingProfileId,
      costSourcePricingProfileName: quotePart?.costSourcePricingProfileName,
      costSourcePricingProfilePricingType: quotePart?.costSourcePricingProfilePricingType,
      costSourceQuoteExternalNumber: quotePart?.costSourceQuoteExternalNumber ?? null,
      costSourceQuoteId: quotePart?.costSourceQuoteId ?? null,
      costSourceQuoteStartDate: quotePart?.costSourceQuoteStartDate ?? null,
      costSourceQuoteValidUntil: quotePart?.costSourceQuoteValidUntil ?? null,
      costSourceStatus: quotePart?.costSourceStatus,
      costSourceType: quotePart?.computedCostSourceType,
      defaultPurchaseCost: !isEnteredCostSourceType(quotePart?.computedCostSourceType)
        ? quotePart?.computedPurchaseCost
        : null,
      // Seeds the raw base cost for a drawer that opens without a /prices call (Update Margin, and
      // the first Overwrite toggle-off on Update Line), so the wire carries the 4-decimal value the
      // BE matches on. Any /prices response overwrites it via mapPricesUomToCostData.
      // Arrives on the quote part via the customer-parts list payload, so it is only populated
      // while Quote_CustomerPartListManager::getFields() selects external_purchase_cost.
      externalPurchaseCost: Number(quotePart?.externalPurchaseCost) || null,
      externalSpaCost: quotePart?.externalSpaCost,
      externalSpaCostEndDate: quotePart?.externalSpaCostEndDate,
      externalSpaCostRegistrationNum: quotePart?.externalSpaCostRegistrationNum,
      externalSpaCostSource: quotePart?.externalSpaCostSource,
      externalSpaCostSourceName: quotePart?.externalSpaCostSourceName,
      externalSpaCostUom: quotePart?.uom,
      selectedSpa: mapQuotePartToSelectedSpa(quotePart),
    },
    unifiedPartStatus: quotePart?.ddpItemFlag
      ? QuotePartUnifiedStatus.ACTIVE
      : QuotePartUnifiedStatus.INACTIVE,
  }),

  mapQuotePartToPriceData: (quotePart: ICustomerQuotePart): IQuotePartPriceData => {
    if (!quotePart?.id) {
      return {};
    }

    return {
      externalSellPriceEndDate: quotePart?.externalSellPriceEndDate,
      externalSellPriceSource: quotePart?.externalSellPriceSource,
      externalSellPriceSourceName: quotePart?.externalSellPriceSourceName,
      priceLocked: QuotePartHelper.isPriceLocked(quotePart),
      priceQuantityDiscountsApplied: quotePart?.priceQuantityDiscountsApplied,
      priceSourceDiscountType: quotePart?.priceSourceDiscountType,
      priceSourceId: quotePart?.priceSourceId,
      priceSourcePricingProfileId: quotePart?.priceSourcePricingProfileId,
      priceSourcePricingProfileName: quotePart?.priceSourcePricingProfileName,
      priceSourceStatus: quotePart?.priceSourceStatus,
      priceSourceType: quotePart?.priceSourceType,
    };
  },

  mapPricesUomToPriceData: (
    pricesUom: Record<string, unknown>,
    isAlternateVersionEnabled: boolean
  ): IQuotePartPriceData => {
    const priceSourceType = pricesUom?.priceSourceType as PricingSourceType;

    return {
      externalSellPriceEndDate: pricesUom?.externalSellPriceEndDate as string,
      externalSellPriceSource: pricesUom?.externalSellPriceSource as string,
      externalSellPriceSourceName: pricesUom?.externalSellPriceSourceName as string,
      // Mirrors QuotePartHelper.isPriceLocked: the lock only applies to an external source on an
      // alternate-version company. Both guards are required — PriceLockTooltip ORs this flag with
      // the canonical helper, so a true here short-circuits past the helper's own check.
      priceLocked:
        isAlternateVersionEnabled &&
        PricingSourceType.EXTERNAL === priceSourceType &&
        !!pricesUom?.externalPriceLocked,
      priceQuantityDiscountsApplied: pricesUom?.priceQuantityDiscountsApplied ? 1 : 0,
      priceSourceDiscountType: pricesUom?.priceSourceDiscountType as PricingSourceDiscountType,
      priceSourceId: pricesUom?.priceSourceId as number,
      priceSourcePricingProfileId: pricesUom?.priceSourcePricingProfileId as number,
      priceSourcePricingProfileName: pricesUom?.priceSourcePricingProfileName as string,
      priceSourceStatus: pricesUom?.priceSourceStatus as PricingSourceStatus,
      priceSourceType,
    };
  },

  sanitizeCostDataForSourceType: (
    costData: IQuotePartCostData,
    costSourceType: PricingSourceType | null
  ): IQuotePartCostData => {
    const nextCostData = {
      ...costData,
      costSourceType,
      // Kept under Overwrite, the agreement would show as applied under a manual label.
      selectedSpa: PricingSourceType.SELECTED_SPA === costSourceType ? costData?.selectedSpa : null,
    };

    if (
      costSourceType === PricingSourceType.CUSTOM ||
      costSourceType === PricingSourceType.SELECTED_SPA ||
      costSourceType === PricingSourceType.EXTERNAL
    ) {
      return {
        ...nextCostData,
        costQuantityDiscountsApplied: null,
        costSourceDiscountType: null,
        costSourceDiscountApplyTo: null,
        costSourceId: null,
        costSourcePricingProfileId: null,
        costSourcePricingProfileName: null,
        ...EMPTY_VENDOR_QUOTE_COST_DATA,
        costSourceStatus: null,
      };
    }

    return nextCostData;
  },

  sanitizePriceDataForSourceType: (
    priceData: IQuotePartPriceData,
    priceSourceType: PricingSourceType | null
  ): IQuotePartPriceData => {
    const nextPriceData = {
      ...priceData,
      priceSourceType,
    };

    if (
      priceSourceType === PricingSourceType.CUSTOM ||
      priceSourceType === PricingSourceType.EXTERNAL
    ) {
      return {
        ...nextPriceData,
        priceSourceDiscountType: null,
        priceQuantityDiscountsApplied: null,
        priceSourceId: null,
        priceSourcePricingProfileId: null,
        priceSourcePricingProfileName: null,
        priceSourceStatus: null,
      };
    }

    return nextPriceData;
  },

  /**
   * /quote/part/prices tags an echoed pick cost CUSTOM; adopting it would convert the line to
   * manual, so a picked line takes only the standard-cost and price side.
   */
  resolveSelectedSpaCostResponse: (
    currentCostData: IQuotePartCostData,
    responseCostData: IQuotePartCostData
  ): {
    costData: IQuotePartCostData;
    purchaseCost: number | null;
    shouldAdoptCostSourceType: boolean;
  } => {
    const selectedSpa = currentCostData?.selectedSpa;

    if (!holdsSelectedSpa(currentCostData)) {
      return { costData: responseCostData, purchaseCost: null, shouldAdoptCostSourceType: true };
    }

    return {
      costData: {
        ...responseCostData,
        costSourceType: PricingSourceType.SELECTED_SPA,
        selectedSpa,
      },
      purchaseCost: selectedSpa.spaCost,
      // The caller must not set the Overwrite toggle from this response.
      shouldAdoptCostSourceType: false,
    };
  },

  doesPurchaseCostExceedStandard: (
    purchaseCost: number | string | null | undefined,
    standardCost: number | string | null | undefined,
    costSourceType?: PricingSourceType | string | null
  ): boolean => {
    if (!isEnteredCostSourceType(costSourceType as PricingSourceType)) {
      return false;
    }

    const pc = Number(purchaseCost);
    const sc = Number(standardCost);

    return pc > 0 && sc > 0 && pc > sc;
  },
};
